// Contrat des CSV produits par le pipeline du POC (poc/docs/donnees/csv-normalises.md).
//
// Un schéma par table, ligne par ligne. Les colonnes en trop sont tolérées (le
// pipeline peut en ajouter) ; les colonnes manquantes et les valeurs hors domaine
// ne le sont pas. Les colonnes dynamiques — `cible_*` et les colonnes de leviers —
// sont contrôlées dans portefeuille-csv.ts, contre le référentiel.

import { z } from "zod";

// Messages par défaut en français : ils remontent tels quels jusqu'à l'écran d'erreur.
z.config(z.locales.fr());

const requis = z.string().min(1, "valeur obligatoire");
const libre = z.string();
const vide = z.literal("");

export const ouiNon = z.enum(["oui", "non"], { error: "attendu « oui » ou « non »" });

const refProjet = z.string().regex(/^PRJ-\d{3,}$/, "référence attendue au format PRJ-001");
const dateIsoOuVide = z.union([vide, z.iso.date()], {
  error: "date attendue au format AAAA-MM-JJ, ou vide",
});
const entierOuVide = z.union([vide, z.string().regex(/^\d+$/)], {
  error: "entier positif attendu, ou vide",
});

export const ligneProjet = z.object({
  ref: refProjet,
  id: requis,
  acronyme: requis,
  titre: libre,
  porteur: libre,
  type_porteur: libre,
  portee: z.enum(["filiere", "transversal", ""], {
    error: "attendu « filiere », « transversal » ou vide",
  }),
  filiere: libre,
  filieres_impliquees: libre,
  plan_action: libre,
  bioagresseurs_communs: libre,
  bioagresseurs_latins: libre,
  date_debut: dateIsoOuVide,
  date_fin: dateIsoOuVide,
  duree_mois: entierOuVide,
  // Pas de contrôle bloquant ici : un lien illisible est écarté et signalé
  // (portefeuille-csv.ts), il ne doit pas empêcher d'afficher le portefeuille.
  lien_ecophytopic: libre,
});

export const ligneLeviersProjet = z.object({
  ref_projet: refProjet,
  id_projet: libre,
});

export const ligneReferentielLevier = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/, "identifiant attendu en minuscules, chiffres et _"),
  libelle: requis,
  libelle_source: libre,
  exemples: libre,
  // Colonne ajoutée au POC le 15/09/2026 : vide vaut « levier ».
  nature: z.enum(["levier", "indicateur", ""], {
    error: "attendu « levier », « indicateur » ou vide",
  }),
});

export const ligneCulture = z.object({
  ref_projet: refProjet,
  culture: requis,
  culture_cle: libre,
});

export const lignePartenaire = z.object({
  ref_projet: refProjet,
  partenaire: requis,
  partenaire_cle: libre,
  type_structure: libre,
});

export type LigneProjet = z.infer<typeof ligneProjet>;
export type LigneReferentielLevier = z.infer<typeof ligneReferentielLevier>;
export type LigneCulture = z.infer<typeof ligneCulture>;
export type LignePartenaire = z.infer<typeof lignePartenaire>;
